import { useState, useCallback, useEffect } from 'react';
import { CreateBusinessData, BusinessAddress } from '../business';
import { validateBusinessForm } from '../utils/validation';
import { NumberUtils, PhoneUtils } from '@cleanpro/common-ui';
import { BUSINESS_STATUS } from '../constants';

const STORAGE_KEY = 'gositback_create_business_form';

/**
 * Initial form data state for creating a new business
 */
const initialFormData: CreateBusinessData = {
  businessName: '',
  ownerName: '',
  email: '',
  phone: '',
  address: {
    addressLine1: '',
    addressLine2: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: ''
  },
  businessType: 'independent_contractor',
  servicesOffered: [],
  hourlyRate: 0,
  insuranceProvider: '',
  policyNumber: '',
  expirationDate: '',
  availability: {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false
  }
};

/**
 * Custom hook for managing the create business form state and validation
 */
export function useCreateBusinessForm() {
  // Initialize state with persisted data from sessionStorage if available
  const [formData, setFormData] = useState<CreateBusinessData>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...initialFormData, ...parsed };
      }
    } catch (error) {
      console.error('Failed to restore form data:', error);
    }
    return initialFormData;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Persist form data to sessionStorage with debounce
  // sessionStorage survives tab switches but clears when tab closes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        const hasData = formData.businessName || 
                       formData.ownerName || 
                       formData.email || 
                       formData.phone;
        
        if (hasData) {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
        }
      } catch (error) {
        console.error('Failed to persist form data:', error);
      }
    }, 300); // 300ms debounce for fast persistence during tab switches

    return () => clearTimeout(timeoutId);
  }, [formData]);

  /**
   * Handles changes to simple form fields
   */
  const handleInputChange = useCallback((field: keyof CreateBusinessData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Handles changes to address fields
   */
  const handleAddressChange = useCallback((field: keyof BusinessAddress, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Toggles a service in the servicesOffered array
   */
  const handleServiceToggle = useCallback((service: string) => {
    setFormData(prev => ({
      ...prev,
      servicesOffered: prev.servicesOffered.includes(service)
        ? prev.servicesOffered.filter(s => s !== service)
        : [...prev.servicesOffered, service]
    }));
    setTouched(prev => ({ ...prev, servicesOffered: true }));
    
    // Clear error for services
    if (errors.servicesOffered) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.servicesOffered;
        return newErrors;
      });
    }
  }, [errors.servicesOffered]);

  /**
   * Handles changes to availability checkboxes
   */
  const handleAvailabilityChange = useCallback((day: keyof CreateBusinessData['availability'], checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: checked
      }
    }));
    setTouched(prev => ({ ...prev, availability: true }));
    
    // Clear error for availability
    if (errors.availability) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.availability;
        return newErrors;
      });
    }
  }, [errors.availability]);

  /**
   * Formats phone number on blur
   */
  const handlePhoneBlur = useCallback(() => {
    if (formData.phone) {
      const formatted = PhoneUtils.format(formData.phone);
      setFormData(prev => ({ ...prev, phone: formatted }));
    }
  }, [formData.phone]);

  /**
   * Formats postal code on blur
   */
  const handlePostalCodeBlur = useCallback(() => {
    if (formData.address.addressPostalCode) {
      const formatted = NumberUtils.formatPostalCode(formData.address.addressPostalCode);
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, addressPostalCode: formatted }
      }));
    }
  }, [formData.address.addressPostalCode]);

  /**
   * Validates the entire form and returns validation errors
   */
  const validate = useCallback((): boolean => {
    const validationErrors = validateBusinessForm(formData);
    
    // Convert ValidationError[] to Record<string, string>
    const errorMap: Record<string, string> = {};
    for (const error of validationErrors) {
      errorMap[error.field] = error.message;
    }
    
    setErrors(errorMap);
    return validationErrors.length === 0;
  }, [formData]);

  /**
   * Prepares the form data for submission
   */
  const prepareSubmissionData = useCallback(() => {
    return {
      businessName: formData.businessName,
      ownerName: formData.ownerName,
      email: formData.email,
      phone: formData.phone,
      address: {
        addressLine1: formData.address.addressLine1,
        addressLine2: formData.address.addressLine2,
        addressCity: formData.address.addressCity,
        addressState: formData.address.addressState,
        addressPostalCode: formData.address.addressPostalCode
      },
      businessType: formData.businessType,
      status: BUSINESS_STATUS.PENDING,
      contractStartDate: new Date().toISOString().split('T')[0],
      servicesOffered: formData.servicesOffered,
      hourlyRate: formData.hourlyRate,
      availability: formData.availability,
      insuranceInfo: formData.insuranceProvider ? {
        provider: formData.insuranceProvider,
        policyNumber: formData.policyNumber || '',
        expirationDate: formData.expirationDate || ''
      } : undefined
    };
  }, [formData]);

  /**
   * Resets the form to initial state and clears sessionStorage
   */
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setTouched({});
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear form data:', error);
    }
  }, []);

  /**
   * Clears the persisted form data from sessionStorage
   */
  const clearPersistedData = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear persisted data:', error);
    }
  }, []);

  return {
    formData,
    errors,
    touched,
    handleInputChange,
    handleAddressChange,
    handleServiceToggle,
    handleAvailabilityChange,
    handlePhoneBlur,
    handlePostalCodeBlur,
    validate,
    prepareSubmissionData,
    resetForm,
    clearPersistedData
  };
}
